import { BACKEND_URL } from "./env.js";

export type MediaKind = "photo" | "video" | "audio";

export interface MediaRef {
  kind: MediaKind;
  file_id: string;
}

export interface PrepareShareResponse {
  prepared_message_id: string;
  expires_at: string;
}

export interface UploadMediaResponse {
  file_id: string;
  kind: MediaKind;
  /**
   * Optional base64 data URL of a small server-rendered preview, sent
   * back by the bot for photos. Used as a fallback thumbnail on
   * platforms (Telegram Android) where the picked File can't be read
   * locally. Always present for successful photo uploads when the bot
   * could fetch the preview from Telegram's CDN.
   */
  thumb_dataurl?: string;
}

export interface BackendError {
  /** Stable machine code, e.g. `empty_post`. */
  error: string;
  /**
   * Server-issued correlation id (UUID). Forwarded by the bot on every
   * `/api/upload-media` response so a failed upload in the browser
   * console can be looked up directly in the bot's K8s pod logs.
   */
  request_id?: string;
  /** Optional extras returned by the server (e.g. `limit` for `too_long`). */
  [k: string]: unknown;
}

export class PrepareShareError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly extra: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = "PrepareShareError";
  }
}

export class UploadMediaError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly extra: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = "UploadMediaError";
  }
}

export class NetworkError extends Error {
  constructor(public readonly cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "NetworkError";
  }
}

/**
 * POST the current draft to the backend's `/api/prepare-share` endpoint.
 *
 * `init_data` is forwarded as the `X-Telegram-Init-Data` header (matches
 * the bot's middleware contract). On any non-2xx response we throw a
 * `PrepareShareError` carrying the server's stable error code so callers
 * can map it to user-facing copy.
 *
 * Pass `media` to attach a previously-uploaded file (see {@link uploadMedia}).
 */
export async function prepareShare(opts: {
  state: unknown;
  initData: string;
  media?: MediaRef;
  signal?: AbortSignal;
}): Promise<PrepareShareResponse> {
  let res: Response;
  try {
    res = await fetch(`${BACKEND_URL}/api/prepare-share`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Telegram-Init-Data": opts.initData,
      },
      body: JSON.stringify(
        opts.media
          ? { state: opts.state, media: opts.media }
          : { state: opts.state },
      ),
      signal: opts.signal,
    });
  } catch (err) {
    throw new NetworkError(err);
  }

  if (!res.ok) {
    let body: BackendError = { error: "internal" };
    try {
      body = (await res.json()) as BackendError;
    } catch {
      /* malformed body — fall through with default */
    }
    const { error, ...extra } = body;
    throw new PrepareShareError(error || "internal", res.status, extra);
  }

  return (await res.json()) as PrepareShareResponse;
}

/**
 * Transient HTTP statuses where retrying is likely to help: gateway/upstream
 * issues from the bot calling Telegram (502/503/504) and rate limiting (429).
 * 4xx codes from our own validation (400/409/413) are not retryable.
 */
const RETRYABLE_HTTP_STATUSES = new Set([429, 502, 503, 504]);
const UPLOAD_MAX_ATTEMPTS = 3;
const UPLOAD_BASE_DELAY_MS = 500;

function isRetryableUploadError(err: unknown): boolean {
  if (err instanceof NetworkError) return true;
  if (err instanceof UploadMediaError) {
    return RETRYABLE_HTTP_STATUSES.has(err.status);
  }
  return false;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason as Error);
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason as Error);
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function uploadMediaOnce(opts: {
  file: File;
  kind: MediaKind;
  initData: string;
  signal?: AbortSignal;
}): Promise<UploadMediaResponse> {
  const form = new FormData();
  form.set("kind", opts.kind);
  form.set("file", opts.file);

  let res: Response;
  try {
    res = await fetch(`${BACKEND_URL}/api/upload-media`, {
      method: "POST",
      headers: {
        "X-Telegram-Init-Data": opts.initData,
      },
      body: form,
      signal: opts.signal,
    });
  } catch (err) {
    throw new NetworkError(err);
  }

  if (!res.ok) {
    let body: BackendError = { error: "internal" };
    try {
      body = (await res.json()) as BackendError;
    } catch {
      /* malformed body */
    }
    const { error, ...extra } = body;
    throw new UploadMediaError(error || "internal", res.status, extra);
  }

  return (await res.json()) as UploadMediaResponse;
}

/**
 * Upload a media file to the bot, which sends it through the user's bot
 * DM (scratch chat) and returns a Telegram `file_id` we can later reference
 * in a prepared inline message. The upload message is auto-deleted from
 * the user's chat by the bot (best-effort).
 *
 * Transient failures (network errors, 429/5xx from the bot's call to
 * Telegram) are retried automatically with exponential backoff + jitter
 * up to {@link UPLOAD_MAX_ATTEMPTS} attempts. User-facing 4xx errors
 * (invalid mime, file too large, bot DM unavailable) are surfaced
 * immediately without retry.
 */
export async function uploadMedia(opts: {
  file: File;
  kind: MediaKind;
  initData: string;
  signal?: AbortSignal;
  /** Notified on each transient failure so callers can update UI/logs. */
  onRetry?: (attempt: number, err: unknown) => void;
}): Promise<UploadMediaResponse> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
    try {
      return await uploadMediaOnce(opts);
    } catch (err) {
      lastErr = err;
      if (!isRetryableUploadError(err) || attempt === UPLOAD_MAX_ATTEMPTS) {
        throw err;
      }
      opts.onRetry?.(attempt, err);
      const backoff = UPLOAD_BASE_DELAY_MS * 2 ** (attempt - 1);
      const jitter = Math.random() * 200;
      await sleep(backoff + jitter, opts.signal);
    }
  }
  throw lastErr as Error;
}
